<script setup lang="ts">
import { Checkbox, Label, ZhSystemLabel } from '@zerohuman/ui'
import type { RoleShareStray, RoleShareTask } from '@/api'
import { decidable, listingAction, listingLabel, strayLabel } from '@/lib/catalogListing'

/**
 * A role's tasks as its catalog listing would hold them, each with a tick: ticked goes to the catalog (or stays,
 * brought up to date), unticked stays private (or is withdrawn). Each row says what saving would do. Strays, entries
 * still on the role whose tasks no longer belong there, are listed under it with what will happen to them.
 */
const props = defineProps<{
    tasks: RoleShareTask[]
    strays: RoleShareStray[]
    ticked: Set<string>
    /** Whether this company has the role in the catalog, so a public task elsewhere is said to join it. */
    rolePublic: boolean
    disabled?: boolean
    /** Refusals from the last save, by task id or stray entry id. */
    errors?: Record<string, string>
    /** Prefixes the rows' ids and test ids, so two listings on one screen stay apart. */
    idPrefix: string
}>()
const emit = defineEmits<{ toggle: [taskId: string, on: boolean] }>()

function tone(task: RoleShareTask): 'muted' | 'system' {
    return listingAction(task, props.ticked.has(task.id)) === null ? 'muted' : 'system'
}
</script>

<template>
    <div class="space-y-2">
        <p v-if="!tasks.length && !strays.length" class="text-muted-foreground text-sm">No tasks run as this role yet.</p>
        <ul v-else class="space-y-2">
            <li
                v-for="task in tasks"
                :key="task.id"
                class="space-y-1 text-sm"
                :data-testid="`${idPrefix}-task-${task.slug}`"
            >
                <div class="flex flex-wrap items-center gap-2">
                    <template v-if="decidable(task)">
                        <Checkbox
                            :id="`${idPrefix}-tick-${task.id}`"
                            :model-value="ticked.has(task.id)"
                            :disabled="disabled"
                            :data-testid="`${idPrefix}-tick-${task.slug}`"
                            @update:model-value="emit('toggle', task.id, $event === true)"
                        />
                        <Label :for="`${idPrefix}-tick-${task.id}`" class="font-medium">{{ task.name }}</Label>
                    </template>
                    <span v-else class="text-muted-foreground">{{ task.name }}</span>
                    <ZhSystemLabel as="span" :tone="tone(task)" :data-testid="`${idPrefix}-state-${task.slug}`">
                        {{ listingLabel(task, ticked.has(task.id), rolePublic) }}
                    </ZhSystemLabel>
                </div>
                <p v-if="errors?.[task.id]" class="text-destructive text-xs" role="alert">{{ errors[task.id] }}</p>
            </li>
            <li
                v-for="stray in strays"
                :key="stray.entryId"
                class="space-y-1 text-sm"
                :data-testid="`${idPrefix}-stray-${stray.slug}`"
            >
                <div class="flex flex-wrap items-center gap-2">
                    <span class="text-muted-foreground">{{ stray.name }}</span>
                    <ZhSystemLabel as="span" tone="system">{{ strayLabel(stray) }}</ZhSystemLabel>
                </div>
                <p v-if="errors?.[stray.entryId]" class="text-destructive text-xs" role="alert">
                    {{ errors[stray.entryId] }}
                </p>
            </li>
        </ul>
    </div>
</template>
